/**
 * "Get your university listed" — the banner on /universities and its request
 * form (POST /api/v1/universities/listing-request). The server stores the
 * request, e-mails the team (Reply-To the requester) and answers with a
 * reference; it re-validates every field and names a bad one in `field`,
 * shown inline here. A draft is kept for the tab, so closing the form by
 * mistake loses nothing. Self-contained: renders its own `.tpg` scope.
 */

import React, { useEffect, useRef, useState } from 'react';
import { COUNTRY_ISO } from '../NewHome';
import { EMAIL_RE } from '../ipintel/content';
import {
  ListingField, ListingRequestBody, submitListingRequest, TECH_COUNT_RANGES, TechCountRange, visitorIds,
} from './api';
import { ModalCloseVia, TpModal } from './Modal';
import { TechPageStyles } from './styles';
import { tpClip, tpTrack } from './track';

const DRAFT_KEY = 'unis_listing_draft';
const MSG_MAX = 4000;

type Fields = Record<ListingField, string>;
const EMPTY: Fields = {
  institution: '', website: '', country: '', contact_name: '', role: '', email: '',
  phone: '', tech_count_range: '', tto_portal_url: '', message: '',
};
const ORDER: ListingField[] = ['institution', 'website', 'country', 'contact_name', 'role', 'email', 'phone', 'tech_count_range', 'tto_portal_url', 'message'];
const ID = (k: ListingField) => `tpg-list-${k.replace(/_/g, '-')}`;

const RANGE_LABELS: Record<TechCountRange, string> = {
  '<10': 'Fewer than 10',
  '10-50': '10–50',
  '50-200': '50–200',
  '200-1000': '200–1,000',
  '1000+': 'More than 1,000',
};

const URL_RE = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/\S*)?$/i;

const readDraft = (): Fields => {
  try { return { ...EMPTY, ...(JSON.parse(sessionStorage.getItem(DRAFT_KEY) || '{}') || {}) }; } catch { return { ...EMPTY }; }
};

export const validateListing = (f: Fields): Partial<Record<ListingField, string>> => {
  const e: Partial<Record<ListingField, string>> = {};
  const t = (k: ListingField) => f[k].trim();
  if (t('institution').length < 2) e.institution = 'Please enter the institution’s name.';
  else if (t('institution').length > 200) e.institution = 'Please keep it under 200 characters.';
  if (!URL_RE.test(t('website')) || t('website').length > 300) e.website = 'Please enter the institution’s website, e.g. www.example.edu.';
  if (!t('country')) e.country = 'Please enter the country.';
  else if (t('country').length > 80) e.country = 'Please keep it under 80 characters.';
  if (!t('contact_name')) e.contact_name = 'Please enter your name.';
  else if (t('contact_name').length > 120) e.contact_name = 'Please keep it under 120 characters.';
  if (t('role').length > 120) e.role = 'Please keep it under 120 characters.';
  if (!EMAIL_RE.test(t('email')) || t('email').length > 254) e.email = 'Please enter a valid e-mail address.';
  if (t('phone') && !/^[+()\d\s.-]{6,30}$/.test(t('phone'))) e.phone = 'Please enter a phone number (digits, spaces, + and - only).';
  if (t('tto_portal_url') && (!URL_RE.test(t('tto_portal_url')) || t('tto_portal_url').length > 500)) e.tto_portal_url = 'Please enter a valid web address, or leave it empty.';
  if (t('message').length > MSG_MAX) e.message = `Please keep the message under ${MSG_MAX.toLocaleString('en-US')} characters.`;
  return e;
};

// ---------------------------------------------------------------------------
// Banner
// ---------------------------------------------------------------------------

export const ListingRequestBanner: React.FC<{ position: string; compact?: boolean }> = ({ position, compact }) => {
  const [open, setOpen] = useState(false);
  const onOpen = () => {
    tpTrack('unis_request_banner_click', { position });
    tpTrack('unis_request_open', {});
    setOpen(true);
  };
  return (
    <div className="tpg tpg-banner-host">
      <TechPageStyles />
      {compact ? (
        <button type="button" className="tpg-linkbtn" style={{ fontSize: 14 }} onClick={onOpen}>
          Is your university missing? Get it listed →
        </button>
      ) : (
        <div className="tpg-banner">
          <div>
            <h2>Get your university listed</h2>
            <p>
              Technology transfer offices can showcase their full portfolio to industry on UniversityTech.io — every
              technology gets its own page, and licensing enquiries come straight to your team.
            </p>
          </div>
          <button type="button" className="tpg-btn-primary" onClick={onOpen}>Request a listing</button>
        </div>
      )}
      {open && <ListingRequestModal onClose={() => setOpen(false)} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

export const ListingRequestModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [f, setF] = useState<Fields>(readDraft);
  const [hp, setHp] = useState('');
  const [errors, setErrors] = useState<Partial<Record<ListingField, string>>>({});
  const [formError, setFormError] = useState('');
  const [phase, setPhase] = useState<'form' | 'sending' | 'done'>('form');
  const [ref, setRef] = useState('');
  const sending = useRef(false);
  const dirty = ORDER.some(k => f[k].trim() !== '');

  useEffect(() => {
    if (phase === 'done') return;
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(f)); } catch { /* ignore */ }
  }, [f, phase]);

  const close = (via: ModalCloseVia | 'done') => {
    if (phase === 'sending') return;
    tpTrack('unis_request_close', { via, dirty: phase === 'done' ? false : dirty });
    onClose();
  };

  const set = (k: ListingField) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const v = e.target.value;
    setF(prev => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: undefined }));
    if (formError) setFormError('');
  };

  const focus = (k: ListingField) => setTimeout(() => document.getElementById(ID(k))?.focus(), 0);

  const submit = async () => {
    if (sending.current) return;
    const errs = validateListing(f);
    setErrors(errs);
    setFormError('');
    const bad = ORDER.filter(k => errs[k]);
    if (bad.length) {
      tpTrack('unis_request_error', { stage: 'client', field: bad[0], status: 0 });
      focus(bad[0]);
      return;
    }
    sending.current = true;
    setPhase('sending');
    const t = (k: ListingField) => f[k].trim();
    const opt = (k: ListingField) => (t(k) ? t(k) : undefined);
    const body: ListingRequestBody = {
      institution: t('institution'),
      website: t('website'),
      country: t('country'),
      contact_name: t('contact_name'),
      role: opt('role'),
      email: t('email').toLowerCase(),
      phone: opt('phone'),
      tech_count_range: (opt('tech_count_range') as TechCountRange | undefined),
      tto_portal_url: opt('tto_portal_url'),
      message: opt('message'),
      website_hp: hp,
      page_url: (() => { try { return window.location.href.slice(0, 500); } catch { return undefined; } })(),
      ...visitorIds(),
    };
    tpTrack('unis_request_submit', {});
    const res = await submitListingRequest(body);
    sending.current = false;
    if (res.ok) {
      tpTrack('unis_request_success', { ref: res.ref });
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      setRef(res.ref);
      setPhase('done');
      return;
    }
    setPhase('form');
    tpTrack('unis_request_error', { stage: 'server', field: res.field || null, status: res.status });
    if (res.error === 'rate_limited' || res.status === 429) {
      setFormError('Too many requests from your network just now. Please wait a little and try again — or write to info@universitytech.io.');
      return;
    }
    if (res.field && ORDER.includes(res.field)) {
      setErrors({ [res.field]: res.error } as Partial<Record<ListingField, string>>);
      focus(res.field);
      return;
    }
    setFormError(res.status >= 500 || res.status === 0
      ? `${res.status === 0 ? res.error : 'Something went wrong on our side and your request was not sent.'} Please try again in a moment — or write to info@universitytech.io.`
      : tpClip(res.error, 300) || 'Your request could not be sent.');
  };

  const busy = phase === 'sending';

  const field = (k: ListingField, label: string, opts: { type?: string; auto?: string; placeholder?: string; optional?: boolean; max?: number; list?: string } = {}) => (
    <div>
      <label className="tpg-label" htmlFor={ID(k)}>{label}{opts.optional && <span className="tpg-opt"> (optional)</span>}</label>
      <input
        id={ID(k)}
        type={opts.type || 'text'}
        className={`tpg-input${errors[k] ? ' is-invalid' : ''}`}
        value={f[k]}
        onChange={set(k)}
        autoComplete={opts.auto}
        placeholder={opts.placeholder}
        maxLength={opts.max || 200}
        disabled={busy}
        list={opts.list}
        aria-invalid={!!errors[k]}
        aria-describedby={errors[k] ? `${ID(k)}-err` : undefined}
        required={!opts.optional}
      />
      {errors[k] && <div id={`${ID(k)}-err`} className="tpg-field-error" role="alert">{errors[k]}</div>}
    </div>
  );

  return (
    <div className="tpg" style={{ minHeight: 0, background: 'transparent' }}>
      <TpModal labelledBy="tpg-list-title" onClose={via => close(via)} locked={busy} maxWidth={560}>
        {phase === 'done' ? (
          <div role="status">
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--tpg-success-bg)', color: 'var(--tpg-success)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 16 }} aria-hidden="true">✓</div>
            <div className="tpg-eyebrow">Request received</div>
            <h2 id="tpg-list-title" className="tpg-modal-title">Thank you — we&apos;ll be in touch</h2>
            <p className="tpg-muted" style={{ marginBottom: 8 }}>
              Your listing request for <strong style={{ color: 'var(--tpg-ink)' }}>{f.institution.trim()}</strong> has reached our team.
              We&apos;ll reply to <strong style={{ color: 'var(--tpg-ink)', wordBreak: 'break-all' }}>{f.email.trim()}</strong>{ref ? <> — reference <strong style={{ color: 'var(--tpg-ink)' }}>{ref}</strong></> : null}.
            </p>
            <button type="button" className="tpg-btn-primary" style={{ width: '100%', marginTop: 20 }} onClick={() => close('done')}>Done</button>
          </div>
        ) : (
          <>
            <div className="tpg-eyebrow">Get listed</div>
            <h2 id="tpg-list-title" className="tpg-modal-title">List your university on UniversityTech.io</h2>
            <p className="tpg-muted" style={{ marginBottom: 20 }}>
              Tell us about your institution and technology transfer office. We review every request and reply within a few working days.
            </p>
            <form noValidate onSubmit={e => { e.preventDefault(); void submit(); }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {field('institution', 'Institution', { auto: 'organization', placeholder: 'University of Example' })}
                <div className="tpg-grid-2">
                  {field('website', 'Website', { type: 'url', auto: 'url', placeholder: 'www.example.edu', max: 300 })}
                  {field('country', 'Country', { auto: 'country-name', placeholder: 'India', max: 80, list: 'tpg-list-countries' })}
                </div>
                <datalist id="tpg-list-countries">
                  {Object.keys(COUNTRY_ISO).sort().map(c => <option key={c} value={c} />)}
                </datalist>
                <div className="tpg-grid-2">
                  {field('contact_name', 'Your name', { auto: 'name', placeholder: 'Ada Lovelace', max: 120 })}
                  {field('role', 'Your role', { auto: 'organization-title', placeholder: 'Licensing manager', optional: true, max: 120 })}
                </div>
                <div className="tpg-grid-2">
                  {field('email', 'Work e-mail', { type: 'email', auto: 'email', placeholder: 'you@example.edu', max: 254 })}
                  {field('phone', 'Phone', { type: 'tel', auto: 'tel', placeholder: '+91 98765 43210', optional: true, max: 30 })}
                </div>
                <div className="tpg-grid-2">
                  <div>
                    <label className="tpg-label" htmlFor={ID('tech_count_range')}>Technologies available <span className="tpg-opt">(optional)</span></label>
                    <select id={ID('tech_count_range')} className="tpg-input" value={f.tech_count_range} onChange={set('tech_count_range')} disabled={busy}>
                      <option value="">Choose a range</option>
                      {TECH_COUNT_RANGES.map(r => <option key={r} value={r}>{RANGE_LABELS[r]}</option>)}
                    </select>
                    {errors.tech_count_range && <div className="tpg-field-error" role="alert">{errors.tech_count_range}</div>}
                  </div>
                  {field('tto_portal_url', 'Technology portal', { type: 'url', placeholder: 'tech.example.edu', optional: true, max: 500 })}
                </div>
                <div>
                  <label className="tpg-label" htmlFor={ID('message')}>Anything else? <span className="tpg-opt">(optional)</span></label>
                  <textarea
                    id={ID('message')}
                    className={`tpg-input${errors.message ? ' is-invalid' : ''}`}
                    value={f.message}
                    onChange={set('message')}
                    rows={4}
                    maxLength={MSG_MAX + 200}
                    placeholder="e.g. We have around 300 technologies across agriculture and medtech."
                    disabled={busy}
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? `${ID('message')}-err` : undefined}
                  />
                  {errors.message && <div id={`${ID('message')}-err`} className="tpg-field-error" role="alert">{errors.message}</div>}
                </div>
                {/* Honeypot: invisible to people, irresistible to form-filling bots. */}
                <div className="tpg-hp" aria-hidden="true">
                  <label htmlFor="tpg-list-website-hp">Website URL</label>
                  <input id="tpg-list-website-hp" name="website_hp" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} />
                </div>
              </div>
              {formError && <div className="tpg-alert tpg-alert-error" role="alert" style={{ marginTop: 16 }}>{formError}</div>}
              <button type="submit" className="tpg-btn-primary" style={{ width: '100%', marginTop: 20 }} disabled={busy}>
                {busy && <span className="tpg-spin" aria-hidden="true" />}
                {busy ? 'Sending…' : 'Send request'}
              </button>
            </form>
            <div className="tpg-muted" style={{ fontSize: 12, textAlign: 'center', marginTop: 12 }}>
              Used only to reply to this request.
            </div>
          </>
        )}
      </TpModal>
    </div>
  );
};

export default ListingRequestBanner;
